import React, { useEffect, useState } from 'react'
import { useI18n } from '@ggs/i18n'
import { get, find, first, size } from 'lodash'
import { useAcademicExpApi, useHcmApi } from '../../../local'
import { Typography } from '@mui/material'
import { Loader } from '../../loader'
import { CourseSectionsList } from '../'
import { useSelector } from '@ggs/store'
import './InstructorView.scss'

/**
 * @typedef {import('../../../definitions').WorkerRecord} WorkerRecord
 * @typedef {import('../../../definitions').CourseSectionExpRecord} CourseSectionExpRecord
 */

/**
 * @typedef {Object} InstructorViewProps
 */

/**
 * Specific instructor detailed view.
 * @return {JSX.Element}
 */
const InstructorView = () => {
  const { t } = useI18n()
  const { getWorker } = useHcmApi()
  const { getCourseSections } = useAcademicExpApi()
  const [loading, setLoading] = useState(false)
  const academicPeriod = useSelector((state) => state.settings.academicPeriod)
  const pageSize = 500

  /**
   * @type {WorkerRecord}
   */
  const defaultWorker = null
  const [worker, setWorker] = useState(defaultWorker)

  /**
   * @type {CourseSectionExpRecord[]}
   */
  const defaultCourseSections = null
  const [courseSections, setCourseSections] = useState(defaultCourseSections)

  const instructorId = get(
    window,
    'drupalSettings.react.instructorId',
    '09993ea756abc85c02bf8abc77c40d30'
  )

  const resolveInstructorData = async () => {
    setLoading(true)
    const workerRecord = await getWorker(instructorId)

    if (workerRecord) {
      const employeeId = workerRecord.employeeId
      const courseSectionsResponse = await getCourseSections({
        pageSize,
        employeeId,
        isNotVisible: false,
        academicPeriodId: [academicPeriod],
      })

      const courseSections = courseSectionsResponse?.pageItems
      if (size(courseSections) > 0) {
        setCourseSections(courseSectionsResponse.pageItems)
      }

      setWorker(workerRecord)
    }

    setLoading(false)
  }

  useEffect(() => {
    resolveInstructorData().then(() => {})
  }, [academicPeriod])

  if (loading || !worker) {
    return <Loader />
  }

  const workerName =
    find(worker.personNames, ((o)=> o.nameType === 'Preferred Name')) ??
    find(worker.personNames, ((o)=> o.nameType === 'Legal Name')) ??
    first(worker.personNames)

  // Prevent that ':' separator is escaped due to conflict with ns separator.
  const tOptions = { nsSeparator: '' }

  return (
    <div className="instructor-view">
      <div className="instructor-view__details">
        <div className="instructor-view__detail-content">
          <h5 className="instructor-view__label">{t('Name:', tOptions)}</h5>
          <div className="instructor-view__detail">
            {`${workerName.givenName} ${workerName.familyName}`}
          </div>
        </div>
        <div className="instructor-view__detail-content">
          <h5 className="instructor-view__label">{t('Office Telephone:', tOptions)}</h5>
          <div className="instructor-view__detail">
            {/* <a href={`tel:${phoneNumber.telephoneNumber}`}>{phoneNumber.telephoneNumber}</a> */}
          </div>
        </div>
        <div className="instructor-view__detail-content">
          <h5 className="instructor-view__label">{t('Email:', tOptions)}</h5>
          <div className="instructor-view__detail">
            {/* <a href={`mailto:${email.emailAddress}`}>{email.emailAddress}</a> */}
          </div>
        </div>
      </div>
      {courseSections && (
        <div className="instructor-view__tables">
          <Typography className="instructor-view__title" variant="h5">
            {t('Taught Sections:', tOptions)}
          </Typography>
          <CourseSectionsList
            courseSections={courseSections}
            sectionViewBasePath="/browse-courses/course-section"
          />
        </div>
      )}
    </div>
  )
}

export default InstructorView
